import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  FileText, Plus, Download, RefreshCw, ChevronRight, ChevronLeft, Check,
  GripVertical, Eye, EyeOff, Lock, Trash2, Copy, Settings2, BarChart3,
  Layers, AlertTriangle, Loader2, CheckCircle2, FileDown, X,
  Calendar, ClipboardList, Shield, Target, Zap, Sparkles,
  ArrowRight, PenLine, Upload, Image, Send, UserCheck, Clock,
  Play, GitCompare, FileBarChart
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { value: 'WPR', label: 'Weekly Progress Report', desc: 'Standard 18-slide weekly report', icon: Calendar },
  { value: 'MPR', label: 'Monthly Progress Report', desc: 'Comprehensive 33+ slide monthly report', icon: ClipboardList },
  { value: 'FLASH_REPORT', label: 'Flash Report', desc: 'Quick executive summary', icon: Zap },
  { value: 'EXECUTIVE_SUMMARY', label: 'Executive Summary', desc: 'Board-level summary', icon: Target },
];

const MODULE_ICONS = {
  PROJECT: Layers, MACRO_SCHEDULE: BarChart3, DCI: FileText, MCI: FileText,
  QPT: FileText, WBS: Settings2, COST_CONTROL: BarChart3, COST_CBS: BarChart3,
  HSE: Shield, RAID: AlertTriangle, SITE_MONITORING: Eye,
  PIPING_WORKFRONT: Settings2, STRUCTURAL_WORKFRONT: Settings2,
  CLM: FileText, SCHEDULE_ROLLUP: BarChart3, '4D_MODEL': Layers,
  VPR: Eye, SCAN3D: Eye, MULTI: Layers, MANUAL: PenLine,
};

const STATUS_STYLES = {
  DRAFT: { bg: '#64748B15', text: '#64748B', label: 'Draft' },
  DATA_COLLECTION: { bg: '#F59E0B15', text: '#F59E0B', label: 'Collecting Data' },
  GENERATING: { bg: '#143A6B15', text: '#143A6B', label: 'Generating' },
  GENERATED: { bg: '#22C55E15', text: '#22C55E', label: 'Generated' },
  FAILED: { bg: '#EF444415', text: '#EF4444', label: 'Failed' },
  REVIEW: { bg: '#0EA5E915', text: '#0EA5E9', label: 'In Review' },
  APPROVED: { bg: '#10B98115', text: '#10B981', label: 'Approved' },
  DISTRIBUTED: { bg: '#EC489915', text: '#EC4899', label: 'Distributed' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.DRAFT;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: s.bg, color: s.text }} data-testid={`status-badge-${status}`}>
      {(status === 'GENERATING' || status === 'DATA_COLLECTION') && <Loader2 className="w-3 h-3 animate-spin" />}
      {s.label}
    </span>
  );
}

function DataPreviewModal({ isOpen, onClose, projectId, module }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && module) {
      setLoading(true);
      api.get(`/v1/projects/${projectId}/reports/data-preview/${module}`)
        .then(res => setData(res.data))
        .catch(() => toast.error('Failed to load preview'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, module, projectId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-xl w-[700px] max-h-[80vh] overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="data-preview-modal">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Data Preview: {module}</h3>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
          ) : data ? (
            <pre className="text-xs whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>{JSON.stringify(data, null, 2)}</pre>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No data available</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: REPORT BUILDER
// ═══════════════════════════════════════════════════════════════
function ReportBuilder({ projectId, templates, onReportCreated }) {
  const [step, setStep] = useState(1);
  const [reportType, setReportType] = useState('WPR');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [periodLabel, setPeriodLabel] = useState('');
  const [reportTitle, setReportTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [createdReport, setCreatedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, module: '' });
  const [uploading, setUploading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const match = templates.find(t => t.reportType === reportType);
    if (match) { setSelectedTemplate(match); setSections(match.sections?.map(s => ({ ...s })) || []); }
  }, [reportType, templates]);

  useEffect(() => {
    const now = new Date();
    const weekNum = Math.ceil(((now - new Date(now.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
    if (reportType === 'WPR') {
      const start = new Date(now); start.setDate(now.getDate() - now.getDay() + 1);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      const label = `Week ${weekNum} (${start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}-${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })})`;
      setPeriodLabel(label); setReportTitle(`Weekly Progress Report - ${label}`);
      setStartDate(start.toISOString().split('T')[0]); setEndDate(end.toISOString().split('T')[0]);
    } else if (reportType === 'MPR') {
      const label = now.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      setPeriodLabel(label); setReportTitle(`Monthly Progress Report - ${label}`);
      setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
      setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);
    } else { setPeriodLabel(''); setReportTitle(''); }
  }, [reportType]);

  const handleCreate = async () => {
    if (!selectedTemplate) return toast.error('Select a template');
    try {
      const res = await api.post(`/v1/projects/${projectId}/reports`, {
        templateId: selectedTemplate._id, reportType, periodLabel,
        reportTitle: reportTitle || `${reportType} Report`, startDate, endDate,
      });
      setCreatedReport(res.data); setSections(res.data.sections || []); setStep(2);
      toast.success('Report created');
    } catch { toast.error('Failed to create report'); }
  };

  const handleToggleSection = (sectionId) => {
    setSections(prev => prev.map(s => s.sectionId === sectionId && !s.isMandatory ? { ...s, isEnabled: !s.isEnabled } : s));
  };

  const handlePhotoUpload = async (e) => {
    if (!createdReport || !selectedSection) return;
    const files = Array.from(e.target.files || []);
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', file.name.replace(/\.[^.]+$/, ''));
      formData.append('area', '');
      try {
        const res = await api.post(`/v1/projects/${projectId}/reports/${createdReport._id}/sections/${selectedSection.sectionId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const photo = res.data.photo;
        setSections(prev => prev.map(s => {
          if (s.sectionId === selectedSection.sectionId) {
            const mc = s.manualContent || { narrativeText: '', photos: [] };
            return { ...s, manualContent: { ...mc, photos: [...(mc.photos || []), photo] } };
          }
          return s;
        }));
        toast.success(`Photo "${file.name}" uploaded`);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    if (!createdReport || !selectedSection) return;
    try {
      await api.delete(`/v1/projects/${projectId}/reports/${createdReport._id}/sections/${selectedSection.sectionId}/photos/${photoId}`);
      setSections(prev => prev.map(s => {
        if (s.sectionId === selectedSection.sectionId) {
          const mc = s.manualContent || { photos: [] };
          return { ...s, manualContent: { ...mc, photos: (mc.photos || []).filter(p => p.photoId !== photoId) } };
        }
        return s;
      }));
      toast.success('Photo deleted');
    } catch { toast.error('Failed to delete photo'); }
  };

  const handleAutoCommentary = async (sectionId) => {
    if (!createdReport) return;
    setAiGenerating(sectionId);
    try {
      const res = await api.post(`/v1/projects/${projectId}/reports/${createdReport._id}/sections/${sectionId}/auto-commentary`);
      const commentary = res.data?.commentary || '';
      setSections(prev => prev.map(s => s.sectionId === sectionId ? { ...s, commentary } : s));
      toast.success('Commentary generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate commentary');
    }
    setAiGenerating(null);
  };

  const handleGenerate = async () => {
    if (!createdReport) return;
    setGenerating(true);
    try {
      await api.post(`/v1/projects/${projectId}/reports/${createdReport._id}/configure-sections`, {
        sections: sections.map(s => ({ sectionId: s.sectionId, isEnabled: s.isEnabled, sectionOrder: s.sectionOrder, commentary: s.commentary || '' })),
      });
    } catch { /* ignore */ }
    for (const sec of sections) {
      if (sec.commentary) {
        try { await api.put(`/v1/projects/${projectId}/reports/${createdReport._id}/sections/${sec.sectionId}/commentary`, { commentary: sec.commentary }); } catch { /* ignore */ }
      }
    }
    try {
      await api.post(`/v1/projects/${projectId}/reports/${createdReport._id}/generate`);
      setStep(3); pollProgress(createdReport._id);
    } catch { toast.error('Failed to start generation'); setGenerating(false); }
  };

  const pollProgress = useCallback((reportId) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/v1/projects/${projectId}/reports/${reportId}/progress`);
        setGenProgress(res.data);
        if (res.data.status === 'GENERATED' || res.data.status === 'FAILED') {
          clearInterval(interval); setGenerating(false);
          if (res.data.status === 'GENERATED') { toast.success(`Report generated! ${res.data.totalSlides} slides`); setStep(4); onReportCreated?.(); }
          else toast.error(`Generation failed: ${res.data.lastError}`);
        }
      } catch { clearInterval(interval); setGenerating(false); }
    }, 1500);
  }, [projectId, onReportCreated]);

  const handleDownload = (type = 'pptx') => {
    if (!createdReport) return;
    const endpoint = type === 'pdf' ? 'download-pdf' : 'download';
    window.open(`${API_BASE}/api/v1/projects/${projectId}/reports/${createdReport._id}/${endpoint}`, '_blank');
  };

  const handleRegenerate = async () => {
    if (!createdReport) return;
    setGenerating(true); setStep(3);
    try { await api.post(`/v1/projects/${projectId}/reports/${createdReport._id}/regenerate`); pollProgress(createdReport._id); }
    catch { toast.error('Failed to regenerate'); setGenerating(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2" data-testid="step-indicator">
        {[{ n: 1, label: 'Setup' }, { n: 2, label: 'Configure' }, { n: 3, label: 'Generate' }, { n: 4, label: 'Download' }].map(({ n, label }) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold`}
              style={{ backgroundColor: step >= n ? '#1E4E8C' : 'var(--card-bg)', border: step >= n ? 'none' : '1px solid var(--border-color)', color: step >= n ? '#fff' : 'var(--text-muted)' }}>
              {step > n ? <Check className="w-4 h-4" /> : n}
            </div>
            <span className="text-xs font-medium hidden sm:inline" style={{ color: step >= n ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
            {n < 4 && <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="rounded-xl p-6 space-y-6" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="step-1-setup">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Create New Report</h3>
            <div>
              <label className="text-sm font-medium mb-3 block" style={{ color: 'var(--text-secondary)' }}>Report Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {REPORT_TYPES.map(rt => {
                  const Icon = rt.icon; const selected = reportType === rt.value;
                  return (
                    <button key={rt.value} onClick={() => setReportType(rt.value)} className="p-4 rounded-xl text-left transition-all"
                      style={{ backgroundColor: selected ? '#1E4E8C10' : 'var(--app-bg)', border: selected ? '2px solid #1E4E8C' : '1px solid var(--border-color)' }} data-testid={`report-type-${rt.value}`}>
                      <Icon className="w-5 h-5 mb-2" style={{ color: selected ? '#1E4E8C' : 'var(--text-muted)' }} />
                      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{rt.label}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{rt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Template</label>
              <select value={selectedTemplate?._id || ''} onChange={e => { const t = templates.find(t => t._id === e.target.value); setSelectedTemplate(t); if (t) setSections(t.sections?.map(s => ({ ...s })) || []); }}
                className="w-full px-4 py-2.5 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="template-select">
                {templates.filter(t => t.reportType === reportType || reportType === 'CUSTOM').map(t => (
                  <option key={t._id} value={t._id}>{t.templateName} ({t.sections?.length || 0} sections)</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Period Label</label>
                <input value={periodLabel} onChange={e => setPeriodLabel(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="period-label-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Start Date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="start-date-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>End Date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="end-date-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Report Title</label>
              <input value={reportTitle} onChange={e => setReportTitle(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="report-title-input" />
            </div>
            <div className="flex justify-end">
              <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: '#1E4E8C' }} data-testid="next-configure-btn">
                Next: Configure Sections <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="step-2-configure">
            <div className="flex gap-5">
              <div className="w-[380px] flex-shrink-0 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Sections ({sections.filter(s => s.isEnabled).length}/{sections.length})</h4>
                </div>
                <div className="max-h-[600px] overflow-y-auto">
                  {sections.sort((a, b) => a.sectionOrder - b.sectionOrder).map(sec => {
                    const Icon = MODULE_ICONS[sec.sourceModule] || FileText;
                    const isSelected = selectedSection?.sectionId === sec.sectionId;
                    return (
                      <div key={sec.sectionId} onClick={() => setSelectedSection(sec)} className="flex items-center gap-2 px-4 py-3 cursor-pointer border-b"
                        style={{ backgroundColor: isSelected ? '#1E4E8C08' : 'transparent', borderColor: 'var(--border-color)', borderLeft: isSelected ? '3px solid #1E4E8C' : '3px solid transparent' }} data-testid={`section-item-${sec.sectionId}`}>
                        <GripVertical className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--text-disabled)' }} />
                        <button onClick={e => { e.stopPropagation(); handleToggleSection(sec.sectionId); }} className="flex-shrink-0" data-testid={`toggle-section-${sec.sectionId}`}>
                          {sec.isMandatory ? <Lock className="w-4 h-4" style={{ color: '#F59E0B' }} /> : sec.isEnabled ? <Eye className="w-4 h-4" style={{ color: '#22C55E' }} /> : <EyeOff className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />}
                        </button>
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
                        <span className={`text-xs font-medium truncate flex-1 ${sec.isEnabled ? '' : 'opacity-50'}`} style={{ color: 'var(--text-primary)' }}>{sec.sectionOrder}. {sec.sectionTitle}</span>
                        {(sec.manualContent?.photos?.length > 0) && <span className="text-[10px] px-1 rounded" style={{ backgroundColor: '#1E4E8C20', color: '#1E4E8C' }}>{sec.manualContent.photos.length}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex-1 rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                {selectedSection ? (
                  <div className="space-y-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedSection.sectionTitle}</h4>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#1E4E8C15', color: '#1E4E8C' }}>{selectedSection.sourceModule}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#143A6B15', color: '#143A6B' }}>{selectedSection.slideType}</span>
                        </div>
                      </div>
                      {selectedSection.sourceModule !== 'MANUAL' && (
                        <button onClick={() => setPreviewModal({ open: true, module: selectedSection.sourceModule })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }} data-testid="preview-data-btn">
                          <Eye className="w-3 h-3" /> Preview Data
                        </button>
                      )}
                    </div>
                    {selectedSection.commentaryEnabled && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Management Commentary</label>
                          <button
                            onClick={() => handleAutoCommentary(selectedSection.sectionId)}
                            disabled={aiGenerating === selectedSection.sectionId}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-90 disabled:opacity-50"
                            style={{ background: 'linear-gradient(135deg, #143A6B, #1E4E8C)', color: '#fff' }}
                            data-testid="auto-commentary-btn"
                          >
                            {aiGenerating === selectedSection.sectionId ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Auto-generate</>
                            )}
                          </button>
                        </div>
                        <textarea value={sections.find(s => s.sectionId === selectedSection.sectionId)?.commentary || ''}
                          onChange={e => { const val = e.target.value; setSections(prev => prev.map(s => s.sectionId === selectedSection.sectionId ? { ...s, commentary: val } : s)); }}
                          rows={4} placeholder={selectedSection.commentaryPlaceholder || 'Add management commentary...'}
                          className="w-full px-3 py-2 rounded-lg text-sm resize-none" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="section-commentary-input" />
                      </div>
                    )}
                    {selectedSection.slideType === 'PHOTO_GALLERY' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Site Photos</label>
                          <div>
                            <input type="file" ref={fileInputRef} multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-file-input" />
                            <button onClick={() => fileInputRef.current?.click()} disabled={uploading || !createdReport}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#1E4E8C' }} data-testid="upload-photos-btn">
                              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload Photos
                            </button>
                          </div>
                        </div>
                        {(() => {
                          const currentSec = sections.find(s => s.sectionId === selectedSection.sectionId);
                          const photos = currentSec?.manualContent?.photos || [];
                          if (photos.length === 0) return <p className="text-xs p-3 rounded-lg" style={{ backgroundColor: 'var(--app-bg)', border: '1px dashed var(--border-color)', color: 'var(--text-muted)' }}>No photos uploaded yet. Upload site photos to include in the report.</p>;
                          return (
                            <div className="grid grid-cols-3 gap-3" data-testid="photos-grid">
                              {photos.map(photo => (
                                <div key={photo.photoId} className="relative rounded-lg overflow-hidden group" style={{ border: '1px solid var(--border-color)' }}>
                                  <div className="w-full h-24 flex items-center justify-center" style={{ backgroundColor: 'var(--app-bg)' }}>
                                    <Image className="w-8 h-8" style={{ color: 'var(--text-disabled)' }} />
                                  </div>
                                  <div className="p-2">
                                    <p className="text-[10px] truncate font-medium" style={{ color: 'var(--text-primary)' }}>{photo.caption || photo.originalName}</p>
                                    {photo.area && <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{photo.area}</p>}
                                  </div>
                                  <button onClick={() => handleDeletePhoto(photo.photoId)} className="absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#EF4444', color: '#fff' }} data-testid={`delete-photo-${photo.photoId}`}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {selectedSection.manualContentEnabled && selectedSection.slideType !== 'PHOTO_GALLERY' && (
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--app-bg)', border: '1px dashed var(--border-color)' }}>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{selectedSection.manualContentPlaceholder || 'Manual content section'}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64" style={{ color: 'var(--text-disabled)' }}>
                    <p className="text-sm">Select a section to configure</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between mt-5">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }} data-testid="back-to-setup-btn">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={handleGenerate} disabled={generating} className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#22C55E' }} data-testid="generate-report-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Generate Report
              </button>
            </div>
            <DataPreviewModal isOpen={previewModal.open} onClose={() => setPreviewModal({ open: false, module: '' })} projectId={projectId} module={previewModal.module} />
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="rounded-xl p-8" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="step-3-generating">
            <div className="text-center space-y-6 max-w-lg mx-auto">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: '#143A6B15' }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#143A6B' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Generating Report</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  {genProgress?.status === 'DATA_COLLECTION' ? 'Collecting data from platform modules...' : genProgress?.status === 'GENERATING' ? 'Building slides, charts, and PDF...' : 'Processing...'}
                </p>
              </div>
              <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--border-color)' }}>
                <div className="h-2 rounded-full transition-all duration-500" style={{ backgroundColor: '#143A6B', width: genProgress?.status === 'DATA_COLLECTION' ? '35%' : genProgress?.status === 'GENERATING' ? '70%' : genProgress?.status === 'GENERATED' ? '100%' : '10%' }} />
              </div>
              <div className="space-y-2 text-left">
                {['Data Collection', 'Chart Rendering', 'PPTX Assembly', 'PDF Conversion'].map((item, i) => {
                  const done = genProgress?.status === 'GENERATED' || (genProgress?.status === 'GENERATING' && i < 2) || (genProgress?.status === 'DATA_COLLECTION' && i < 1);
                  const active = (genProgress?.status === 'DATA_COLLECTION' && i === 0) || (genProgress?.status === 'GENERATING' && i >= 1);
                  return (
                    <div key={item} className="flex items-center gap-3">
                      {done ? <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} /> : active ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#143A6B' }} /> : <div className="w-4 h-4 rounded-full" style={{ border: '1.5px solid var(--border-color)' }} />}
                      <span className="text-sm" style={{ color: done ? '#22C55E' : active ? 'var(--text-primary)' : 'var(--text-disabled)' }}>{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {step === 4 && genProgress && (
          <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="rounded-xl p-8" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="step-4-download">
            <div className="text-center space-y-6 max-w-xl mx-auto">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: '#22C55E15' }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: '#22C55E' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Report Generated Successfully</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  {genProgress.totalSlides} slides | {genProgress.totalCharts} charts | {genProgress.totalTables} tables | {genProgress.outputFileSizeMB} MB | {genProgress.totalGenerationTimeSeconds}s
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button onClick={() => handleDownload('pptx')} className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: '#1E4E8C' }} data-testid="download-pptx-btn">
                  <Download className="w-4 h-4" /> Download PPTX
                </button>
                {genProgress.pdfFileName && (
                  <button onClick={() => handleDownload('pdf')} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: '#EF4444' }} data-testid="download-pdf-btn">
                    <FileBarChart className="w-4 h-4" /> Download PDF
                  </button>
                )}
                <button onClick={handleRegenerate} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium" style={{ border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }} data-testid="regenerate-btn">
                  <RefreshCw className="w-4 h-4" /> Regenerate
                </button>
              </div>
              <button onClick={() => { setStep(1); setCreatedReport(null); setGenProgress(null); }} className="text-sm underline" style={{ color: 'var(--text-muted)' }} data-testid="create-another-btn">Create another report</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: GENERATED REPORTS with Approval Workflow
// ═══════════════════════════════════════════════════════════════
function GeneratedReports({ projectId, reports, loading, onRefresh }) {
  const [distributing, setDistributing] = useState(null);
  const [distRecipients, setDistRecipients] = useState('');

  const handleDownload = (reportId, type = 'pptx') => {
    const endpoint = type === 'pdf' ? 'download-pdf' : 'download';
    window.open(`${API_BASE}/api/v1/projects/${projectId}/reports/${reportId}/${endpoint}`, '_blank');
  };
  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try { await api.delete(`/v1/projects/${projectId}/reports/${reportId}`); toast.success('Deleted'); onRefresh(); } catch { toast.error('Failed'); }
  };
  const handleSubmitReview = async (reportId) => {
    try { await api.post(`/v1/projects/${projectId}/reports/${reportId}/submit-for-review`); toast.success('Submitted for review'); onRefresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleApprove = async (reportId) => {
    try { await api.post(`/v1/projects/${projectId}/reports/${reportId}/approve`, { approvedByName: 'Current User' }); toast.success('Approved'); onRefresh(); } catch { toast.error('Failed'); }
  };
  const handleDistribute = async (reportId) => {
    const recipients = distRecipients.split(',').map(e => e.trim()).filter(Boolean).map(email => ({ name: email.split('@')[0], email, role: 'PMT' }));
    if (recipients.length === 0) return toast.error('Enter at least one email');
    try {
      await api.post(`/v1/projects/${projectId}/reports/${reportId}/distribute`, { recipients });
      toast.success(`Distributed to ${recipients.length} recipients`);
      setDistributing(null); setDistRecipients(''); onRefresh();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  if (reports.length === 0) return (
    <div className="text-center py-16" data-testid="no-reports">
      <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-disabled)' }} />
      <h3 className="text-base font-medium" style={{ color: 'var(--text-secondary)' }}>No reports generated yet</h3>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="reports-list">
      {reports.map(r => {
        const gen = r.generation || {};
        const approval = r.approval || {};
        return (
          <div key={r._id} className="rounded-xl p-4 space-y-3" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`report-row-${r._id}`}>
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: r.reportType === 'WPR' ? '#1E4E8C12' : '#143A6B12' }}>
                <FileText className="w-5 h-5" style={{ color: r.reportType === 'WPR' ? '#1E4E8C' : '#143A6B' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{r.reportTitle}</div>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.reportCode}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.reportingPeriod?.periodLabel}</span>
                  {gen.totalSlides && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{gen.totalSlides} slides</span>}
                  {r.version > 1 && <span className="text-xs px-1 rounded" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>v{r.version}</span>}
                </div>
              </div>
              <StatusBadge status={gen.status || 'DRAFT'} />
              <div className="flex items-center gap-1.5">
                {gen.status === 'GENERATED' && (
                  <>
                    <button onClick={() => handleDownload(r._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#1E4E8C15' }} data-testid={`download-btn-${r._id}`} title="Download PPTX">
                      <Download className="w-4 h-4" style={{ color: '#1E4E8C' }} />
                    </button>
                    {gen.pdfFileName && (
                      <button onClick={() => handleDownload(r._id, 'pdf')} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#EF444415' }} data-testid={`pdf-btn-${r._id}`} title="Download PDF">
                        <FileBarChart className="w-4 h-4" style={{ color: '#EF4444' }} />
                      </button>
                    )}
                    <button onClick={() => handleSubmitReview(r._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#0EA5E915' }} title="Submit for Review">
                      <Send className="w-4 h-4" style={{ color: '#0EA5E9' }} />
                    </button>
                  </>
                )}
                {gen.status === 'REVIEW' && (
                  <button onClick={() => handleApprove(r._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#10B98115' }} title="Approve">
                    <UserCheck className="w-4 h-4" style={{ color: '#10B981' }} />
                  </button>
                )}
                {(gen.status === 'APPROVED' || gen.status === 'GENERATED' || gen.status === 'REVIEW') && (
                  <button onClick={() => setDistributing(distributing === r._id ? null : r._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#EC489915' }} title="Distribute">
                    <Send className="w-4 h-4" style={{ color: '#EC4899' }} />
                  </button>
                )}
                <button onClick={() => handleDelete(r._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#EF444415' }} data-testid={`delete-btn-${r._id}`}>
                  <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
                </button>
              </div>
            </div>
            {/* Approval info */}
            {(approval.reviewedByName || approval.approvedByName) && (
              <div className="flex items-center gap-4 pl-14 text-xs" style={{ color: 'var(--text-muted)' }}>
                {approval.reviewedByName && <span>Reviewed by: {approval.reviewedByName}</span>}
                {approval.reviewComments && <span>"{approval.reviewComments}"</span>}
                {approval.approvedByName && <span>Approved by: {approval.approvedByName}</span>}
              </div>
            )}
            {/* Distribution panel */}
            {distributing === r._id && (
              <div className="ml-14 p-3 rounded-lg flex items-center gap-3" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }} data-testid="distribute-panel">
                <input value={distRecipients} onChange={e => setDistRecipients(e.target.value)} placeholder="client@example.com, manager@example.com"
                  className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="distribute-emails-input" />
                <button onClick={() => handleDistribute(r._id)} className="px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#EC4899' }} data-testid="distribute-send-btn">Distribute</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: TEMPLATES
// ═══════════════════════════════════════════════════════════════
function TemplatesTab({ projectId, templates, loading, onRefresh }) {
  const handleDuplicate = async (templateId) => {
    try { await api.post(`/v1/projects/${projectId}/reports/templates/${templateId}/duplicate`); toast.success('Duplicated'); onRefresh(); } catch { toast.error('Failed'); }
  };
  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/v1/projects/${projectId}/reports/templates/${templateId}`); toast.success('Deleted'); onRefresh(); } catch { toast.error('Failed'); }
  };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="templates-list">
      {templates.map(t => (
        <div key={t._id} className="rounded-xl p-4 flex items-center gap-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`template-row-${t._id}`}>
          <div className="p-2.5 rounded-lg" style={{ backgroundColor: '#F59E0B12' }}><Layers className="w-5 h-5" style={{ color: '#F59E0B' }} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t.templateName}</div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#1E4E8C15', color: '#1E4E8C' }}>{t.reportType}</span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.sections?.length || 0} sections</span>
              {t.isDefault && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#22C55E15', color: '#22C55E' }}>Default</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleDuplicate(t._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#143A6B15' }} data-testid={`duplicate-template-${t._id}`}><Copy className="w-4 h-4" style={{ color: '#143A6B' }} /></button>
            {!t.isDefault && <button onClick={() => handleDelete(t._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#EF444415' }}><Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: SCHEDULES
// ═══════════════════════════════════════════════════════════════
function SchedulesTab({ projectId, templates }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ reportType: 'WPR', templateId: '', scheduleName: '', scheduleType: 'WEEKLY', dayOfWeek: 'monday', timeOfDay: '06:00', dayOfMonth: 1 });

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/v1/reports/schedules'); setSchedules(res.data || []); } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const handleCreate = async () => {
    const tplId = form.templateId || templates.find(t => t.reportType === form.reportType)?._id;
    if (!tplId) return toast.error('Select a template');
    try {
      await api.post('/v1/reports/schedules', { ...form, templateId: tplId, projectId });
      toast.success('Schedule created'); setShowCreate(false); fetchSchedules();
    } catch { toast.error('Failed'); }
  };

  const handleRunNow = async (id) => {
    try { await api.post(`/v1/reports/schedules/${id}/run-now`); toast.success('Generation triggered'); } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/v1/reports/schedules/${id}`); toast.success('Deleted'); fetchSchedules(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="schedules-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: '#1E4E8C' }} data-testid="create-schedule-btn">
          <Plus className="w-4 h-4" /> New Schedule
        </button>
      </div>

      {showCreate && (
        <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="create-schedule-form">
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Create Report Schedule</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Schedule Name</label>
              <input value={form.scheduleName} onChange={e => setForm(p => ({ ...p, scheduleName: e.target.value }))} placeholder="Weekly WPR - Monday 6 AM"
                className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="schedule-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Report Type</label>
              <select value={form.reportType} onChange={e => setForm(p => ({ ...p, reportType: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                <option value="WPR">WPR</option><option value="MPR">MPR</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Frequency</label>
              <select value={form.scheduleType} onChange={e => setForm(p => ({ ...p, scheduleType: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                <option value="WEEKLY">Weekly</option><option value="MONTHLY">Monthly</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>{form.scheduleType === 'WEEKLY' ? 'Day of Week' : 'Day of Month'}</label>
              {form.scheduleType === 'WEEKLY' ? (
                <select value={form.dayOfWeek} onChange={e => setForm(p => ({ ...p, dayOfWeek: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                </select>
              ) : (
                <input type="number" min={1} max={28} value={form.dayOfMonth} onChange={e => setForm(p => ({ ...p, dayOfMonth: parseInt(e.target.value) || 1 }))} className="w-full px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Cancel</button>
            <button onClick={handleCreate} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: '#22C55E' }} data-testid="save-schedule-btn">Create Schedule</button>
          </div>
        </div>
      )}

      {schedules.length === 0 && !showCreate ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No schedules configured. Create one to auto-generate reports.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map(s => (
            <div key={s._id} className="rounded-xl p-4 flex items-center gap-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`schedule-row-${s._id}`}>
              <div className="p-2.5 rounded-lg" style={{ backgroundColor: '#10B98115' }}><Clock className="w-5 h-5" style={{ color: '#10B981' }} /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{s.scheduleName || `${s.reportType} ${s.scheduleType}`}</div>
                <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span>{s.scheduleType === 'WEEKLY' ? `Every ${s.dayOfWeek}` : `${s.dayOfMonth}${['st','nd','rd'][s.dayOfMonth-1] || 'th'} of month`} at {s.timeOfDay}</span>
                  <span>{s.reportType}</span>
                  {s.runCount > 0 && <span>{s.runCount} runs</span>}
                  {s.lastRunAt && <span>Last: {new Date(s.lastRunAt).toLocaleDateString()}</span>}
                </div>
              </div>
              <button onClick={() => handleRunNow(s._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#1E4E8C15' }} data-testid={`run-schedule-${s._id}`} title="Run Now">
                <Play className="w-4 h-4" style={{ color: '#1E4E8C' }} />
              </button>
              <button onClick={() => handleDelete(s._id)} className="p-2 rounded-lg hover:opacity-80" style={{ backgroundColor: '#EF444415' }}>
                <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function ProgressReports() {
  const { projectId } = useProject();
  const [activeTab, setActiveTab] = useState('builder');
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const pid = projectId || 'default-project';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, rptRes] = await Promise.all([
        api.get(`/v1/projects/${pid}/reports/templates`),
        api.get(`/v1/projects/${pid}/reports`),
      ]);
      setTemplates(tplRes.data || []); setReports(rptRes.data || []);
    } catch (err) { console.error('Failed to fetch report data:', err); }
    setLoading(false);
  }, [pid]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { id: 'builder', label: 'Report Builder', icon: Plus },
    { id: 'reports', label: 'Generated Reports', icon: FileText, count: reports.length },
    { id: 'templates', label: 'Templates', icon: Layers, count: templates.length },
    { id: 'schedules', label: 'Schedules', icon: Clock },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="progress-reports-page">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#1E4E8C]/20 to-[#143A6B]/10" style={{ border: '1px solid rgba(56,96,255,0.3)' }}>
              <FileDown className="w-8 h-8 text-[#1E4E8C]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Progress Reporting</h1>
              <p style={{ color: 'var(--text-muted)' }}>Automated WPR & MPR generation with live data from all platform modules</p>
            </div>
          </div>
          <button onClick={fetchData} className="p-2 rounded-lg hover:opacity-80" style={{ border: '1px solid var(--border-color)' }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        <div className="flex items-center gap-1 rounded-xl p-1" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          {tabs.map(tab => {
            const Icon = tab.icon; const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ backgroundColor: active ? '#1E4E8C' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)' }} data-testid={`tab-${tab.id}`}>
                <Icon className="w-4 h-4" />{tab.label}
                {tab.count != null && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: active ? 'rgba(255,255,255,0.2)' : 'var(--border-color)', color: active ? '#fff' : 'var(--text-muted)' }}>{tab.count}</span>}
              </button>
            );
          })}
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="PROGRESS_REPORTS" defaultOpen={false} compact={true} />
        )}

        {activeTab === 'builder' && <ReportBuilder projectId={pid} templates={templates} onReportCreated={fetchData} />}
        {activeTab === 'reports' && <GeneratedReports projectId={pid} reports={reports} loading={loading} onRefresh={fetchData} />}
        {activeTab === 'templates' && <TemplatesTab projectId={pid} templates={templates} loading={loading} onRefresh={fetchData} />}
        {activeTab === 'schedules' && <SchedulesTab projectId={pid} templates={templates} />}
      </div>
    </DashboardLayout>
  );
}
